export interface RestrictedAccessDraft {
  restrictedAccessType: string;
  isSearchHidden: boolean;
  savedAccessFlows: any[];
  savedAccessAdvanced: Record<string, any>;
}

export function cloneRestrictedAccessDraft(
  draft: RestrictedAccessDraft
): RestrictedAccessDraft {
  return {
    restrictedAccessType: draft.restrictedAccessType || "",
    isSearchHidden: !!draft.isSearchHidden,
    savedAccessFlows: [...(draft.savedAccessFlows || [])],
    savedAccessAdvanced: { ...(draft.savedAccessAdvanced || {}) },
  };
}

export function buildDraftFromParentState(state: {
  restrictedAccessType?: string;
  isSearchHidden?: boolean;
  savedAccessFlows?: any[];
  savedAccessAdvanced?: Record<string, any>;
}): RestrictedAccessDraft {
  return cloneRestrictedAccessDraft({
    restrictedAccessType: state.restrictedAccessType || "",
    isSearchHidden: !!state.isSearchHidden,
    savedAccessFlows: state.savedAccessFlows || [],
    savedAccessAdvanced: state.savedAccessAdvanced || {},
  });
}

export interface InitialAccessSnapshot {
  choice: string;
  label: string;
  description: string;
  userCount: number;
  hasAdvanced: boolean;
  advancedLabels: string[];
}

export function getAdvancedRuleLabels(
  adv: Record<string, any> | null | undefined
): string[] {
  const source = adv || {};
  const labels: string[] = [];
  if (source.EnforceNetwork || source.enforceNetwork) {
    labels.push("Network");
  }
  if (source.EnforceGeo || source.enforceGeo) {
    labels.push("Location");
  }
  if (source.EnforceDevice || source.enforceDevice) {
    labels.push("Devices");
  }
  if (source.EnforceCompanyEmail || source.enforceCompanyEmail) {
    labels.push("Company email");
  }
  return labels;
}

export function buildInitialAccessSnapshot(params: {
  choice: string;
  label: string;
  description: string;
  savedAccessFlows?: any[];
  savedAccessAdvanced?: Record<string, any>;
}): InitialAccessSnapshot {
  const advancedLabels = getAdvancedRuleLabels(params.savedAccessAdvanced);
  const userCount = (params.savedAccessFlows || []).length;
  return {
    choice: params.choice || "Public",
    label: params.label || "Public",
    description: params.description || "",
    userCount,
    hasAdvanced: advancedLabels.length > 0,
    advancedLabels,
  };
}

export function shouldEnrichInitialAccessSnapshot(
  current: InitialAccessSnapshot | null,
  next: InitialAccessSnapshot
): boolean {
  if (!current) {
    return true;
  }
  if (current.choice !== next.choice) {
    return false;
  }
  return (
    next.userCount > current.userCount ||
    (next.hasAdvanced && !current.hasAdvanced) ||
    next.advancedLabels.length > current.advancedLabels.length
  );
}

export function hasRestrictedDraftContent(draft: RestrictedAccessDraft | null): boolean {
  if (!draft) {
    return false;
  }
  if ((draft.savedAccessFlows || []).length > 0) {
    return true;
  }
  const adv = draft.savedAccessAdvanced || {};
  return !!(
    adv.EnforceNetwork ||
    adv.enforceNetwork ||
    adv.EnforceGeo ||
    adv.enforceGeo ||
    adv.EnforceDevice ||
    adv.enforceDevice ||
    adv.EnforceCompanyEmail ||
    adv.enforceCompanyEmail ||
    adv.AllowedCidrs ||
    adv.allowedCidrs ||
    adv.AllowedEmailDomains ||
    adv.allowedEmailDomains ||
    adv.AllowedDeviceIds ||
    adv.allowedDeviceIds ||
    adv.GeoFenceJson ||
    adv.geoFenceJson
  );
}
